/**
 * Charge one card once against many invoices (#1345).
 *
 * Built to `threads/bulk-card-charge/mockup.html` (v4) — that file is the
 * contract, and a deviation from it is a bug.
 *
 * ── The shape of the flow ────────────────────────────────────────────────
 *
 *   selection bar  →  pick a card  →  review  →  result
 *
 * Nothing is charged until the review step is confirmed, and the review shows
 * what WILL happen and what WILL NOT, in that order, with numbers. The server
 * re-reads every balance inside its transaction and computes the total
 * itself, so what this screen posts is a list of invoice ids and a choice of
 * card — never an amount.
 *
 * ── All-or-nothing, and the screen says so ───────────────────────────────
 *
 * One transaction settles every invoice or none of them. That is what buys
 * the single line on the customer's statement, and it is also the thing most
 * likely to surprise, so the review warns before it runs and the result says
 * plainly that nothing moved if it declines.
 */
import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, CreditCard, Loader2, Lock, X } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { getStripe, isStripeConfigured } from "@/lib/stripe";
import {
    balanceCents,
    batchResultLine,
    cardExpiry,
    cardLabel,
    chargeButtonLabel,
    money,
    typedConfirmMatches,
    validateBatchSelection,
    type InvoiceLike,
} from "./batch-charge-copy";

export interface SavedCard {
    id: string;
    stripe_payment_method_id: string;
    label: string | null;
    brand: string | null;
    last4: string | null;
    exp_month: number | null;
    exp_year: number | null;
    is_default: boolean;
}

interface PreviewLine {
    id: string;
    invoice_number: string | null;
    site_name: string | null;
    amountCents?: number;
    reasonLabel?: string;
    /** For a card skip: what the bank said, in plain words (#1984). */
    reasonDetail?: string | null;
}

interface PreviewResponse {
    totalCents: number;
    requiresTypedConfirm: boolean;
    selectedCount: number;
    chargeable: PreviewLine[];
    skipped: PreviewLine[];
}

type Step = "card" | "review" | "result";

export function BatchChargeBar({
    customerId,
    customerName,
    selectedIds,
    rows,
    scopeLabel,
    onClear,
    onDone,
}: {
    customerId: string;
    customerName: string | null;
    selectedIds: string[];
    /** The loaded page, so the bar can total the selection without a round trip. */
    rows: InvoiceLike[];
    /** What the selection actually covers, in words. Never implied. */
    scopeLabel: string;
    onClear: () => void;
    onDone: () => void;
}) {
    const [open, setOpen] = useState(false);

    // Optimistic total from the rows on screen. The server's own total is
    // what actually gets charged and is shown on the review step; this is
    // just so the button can carry a number.
    const selectedTotal = useMemo(() => {
        const picked = new Set(selectedIds);
        return rows.filter((r) => picked.has(r.id)).reduce((n, r) => n + balanceCents(r), 0);
    }, [rows, selectedIds]);

    if (selectedIds.length === 0) return null;
    const err = validateBatchSelection(selectedIds.length);

    return (
        <>
            <div className="sticky bottom-4 z-20 mt-3 rounded-xl border border-primary/40 bg-card shadow-[var(--shadow-elevated)]">
                <div className="flex flex-wrap items-center gap-3 p-3">
                    <span className="text-sm font-semibold">
                        {selectedIds.length} invoice{selectedIds.length === 1 ? "" : "s"} selected
                    </span>
                    <span className="min-w-[16rem] flex-1 text-xs text-foreground/70">{scopeLabel}</span>
                    <span className="text-base font-bold tabular-nums">{money(selectedTotal)}</span>
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={onClear}>
                            <X className="mr-1 h-3.5 w-3.5" /> Clear
                        </Button>
                        <Button size="sm" disabled={!!err} onClick={() => setOpen(true)}>
                            <CreditCard className="mr-1 h-4 w-4" /> {chargeButtonLabel(selectedTotal)}
                        </Button>
                    </div>
                    {err && (
                        // Refuses, and says what to do instead. Charging the
                        // first 50 would report success over invoices nobody
                        // paid.
                        <p className="w-full text-xs font-medium text-destructive">{err.message}</p>
                    )}
                </div>
            </div>

            {open && (
                <BatchChargeDialog
                    customerId={customerId}
                    customerName={customerName}
                    invoiceIds={selectedIds}
                    onClose={() => setOpen(false)}
                    onCharged={() => {
                        setOpen(false);
                        onClear();
                        onDone();
                    }}
                />
            )}
        </>
    );
}

function BatchChargeDialog({
    customerId,
    customerName,
    invoiceIds,
    onClose,
    onCharged,
}: {
    customerId: string;
    customerName: string | null;
    invoiceIds: string[];
    onClose: () => void;
    onCharged: () => void;
}) {
    const [step, setStep] = useState<Step>("card");
    const [cards, setCards] = useState<SavedCard[] | null>(null);
    const [chosenCardId, setChosenCardId] = useState<string | null>(null);
    const [useNewCard, setUseNewCard] = useState(false);
    const [newCardPmId, setNewCardPmId] = useState<string | null>(null);
    const [keepNewCard, setKeepNewCard] = useState(true);
    const [newCardLabel, setNewCardLabel] = useState("");
    const [preview, setPreview] = useState<PreviewResponse | null>(null);
    const [typed, setTyped] = useState("");
    const [charging, setCharging] = useState(false);
    const [result, setResult] = useState<{
        charged: boolean;
        totalCents: number;
        count: number;
        reason?: string;
    } | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [checking, setChecking] = useState(false);

    // Load the shelf and the preview together — both are read-only.
    useEffect(() => {
        let live = true;
        (async () => {
            try {
                const [cardRes, previewRes] = await Promise.all([
                    api.get<{ cards: SavedCard[] }>(`/api/customers/${customerId}/payment-methods`),
                    api.post<PreviewResponse>(`/api/customers/${customerId}/invoice-batch/preview`, { invoiceIds }),
                ]);
                if (!live) return;
                setCards(cardRes.cards);
                setPreview(previewRes);
                // Default to the account's default card; an account with no
                // cards goes straight to entry — one less click on the path
                // most often walked.
                const def = cardRes.cards.find((c) => c.is_default) ?? cardRes.cards[0];
                if (def) setChosenCardId(def.id);
                else setUseNewCard(true);
            } catch {
                if (live) setError("Could not load the cards on file for this customer.");
            }
        })();
        return () => {
            live = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId]);

    const cardReady = useNewCard ? Boolean(newCardPmId) : Boolean(chosenCardId);
    const chosenCard = cards?.find((c) => c.id === chosenCardId) ?? null;
    const chosenCardText = useNewCard
        ? "the card you entered"
        : chosenCard
          ? cardLabel(chosenCard)
          : "the card on file";

    /** The card part of a request: a shelf card, or the one just entered. */
    const cardBody = () => (useNewCard ? { paymentMethodId: newCardPmId ?? undefined } : { cardId: chosenCardId });

    // One try per card (#1984): ask again WITH the picked card, so invoices it
    // already declined show as skipped before the operator confirms.
    async function goToReview() {
        setChecking(true);
        setError(null);
        try {
            const fresh = await api.post<PreviewResponse>(`/api/customers/${customerId}/invoice-batch/preview`, {
                invoiceIds,
                ...cardBody(),
            });
            setPreview(fresh);
            setStep("review");
        } catch {
            setError("Could not check these invoices for that card. Nothing was charged.");
        } finally {
            setChecking(false);
        }
    }

    async function runCharge() {
        if (!preview) return;
        setCharging(true);
        setError(null);
        try {
            const res = await api.post<{ charged: boolean; totalCents: number; invoiceCount: number }>(
                `/api/customers/${customerId}/invoice-batch/charge`,
                {
                    invoiceIds,
                    ...(useNewCard
                        ? {
                              paymentMethodId: newCardPmId,
                              saveCard: keepNewCard,
                              cardLabel: keepNewCard ? newCardLabel.trim() || undefined : undefined,
                          }
                        : { cardId: chosenCardId }),
                    ...(preview.requiresTypedConfirm ? { typedConfirm: typed } : {}),
                    // The server charges nothing if its total no longer matches.
                    expectedTotalCents: preview.totalCents,
                },
            );
            setResult({ charged: true, totalCents: res.totalCents, count: res.invoiceCount });
            setStep("result");
        } catch (e) {
            // A decline is a 402 with the reason on it — a real outcome to
            // show, not an error to swallow.
            const body = e instanceof ApiError ? ((e.body ?? {}) as any) : {};
            if (
                (body.error === "BATCH_CHANGED" ||
                    body.error === "CARD_ALREADY_DECLINED" ||
                    body.error === "CARD_NEVER_RETRY") &&
                Array.isArray(body.chargeable)
            ) {
                // Nothing was charged. Show the fresh list and let them look again.
                setPreview(body as PreviewResponse);
                setTyped("");
                setError(body.message || "The list changed. Nothing was charged. Check it again.");
            } else if (body.declined) {
                setResult({
                    charged: false,
                    totalCents: body.totalCents ?? preview.totalCents,
                    count: body.chargeable?.length ?? preview.chargeable.length,
                    reason: body.reason,
                });
                setStep("result");
            } else {
                setError(body.message || "The charge could not be started. Nothing was charged.");
            }
        } finally {
            setCharging(false);
        }
    }

    const confirmBlocked =
        !preview ||
        preview.chargeable.length === 0 ||
        (preview.requiresTypedConfirm && !typedConfirmMatches(typed, preview.totalCents));

    return (
        <Dialog
            open
            onClose={step === "result" && result?.charged ? onCharged : onClose}
            title={
                step === "result"
                    ? result?.charged
                        ? "Charged"
                        : "Not charged"
                    : `Charge a card — ${invoiceIds.length} invoice${invoiceIds.length === 1 ? "" : "s"}${
                          customerName ? `, ${customerName}` : ""
                      }`
            }
            className="max-w-2xl"
        >
            <div className="space-y-4 p-4">
                {error && (
                    <p className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                        {error}
                    </p>
                )}

                {step === "card" && (
                    <CardStep
                        customerId={customerId}
                        cards={cards}
                        chosenCardId={chosenCardId}
                        onChoose={(id) => {
                            setChosenCardId(id);
                            setUseNewCard(false);
                        }}
                        useNewCard={useNewCard}
                        onUseNewCard={() => setUseNewCard(true)}
                        newCardPmId={newCardPmId}
                        onNewCard={setNewCardPmId}
                        keepNewCard={keepNewCard}
                        setKeepNewCard={setKeepNewCard}
                        newCardLabel={newCardLabel}
                        setNewCardLabel={setNewCardLabel}
                    />
                )}

                {step === "review" && preview && (
                    <ReviewStep preview={preview} cardText={chosenCardText} typed={typed} setTyped={setTyped} />
                )}

                {step === "result" && result && <ResultStep result={result} />}
            </div>

            <div className="flex flex-wrap items-center gap-2 border-t border-border/70 bg-muted/40 p-3">
                {step === "card" && (
                    <>
                        <div className="flex-1" />
                        <Button variant="ghost" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button disabled={!cardReady || !preview || checking} onClick={goToReview}>
                            {checking ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Checking…
                                </>
                            ) : (
                                "Next — review the charge"
                            )}
                        </Button>
                    </>
                )}
                {step === "review" && (
                    <>
                        <div className="flex-1" />
                        <Button variant="ghost" onClick={() => setStep("card")} disabled={charging}>
                            Back
                        </Button>
                        <Button disabled={confirmBlocked || charging} onClick={runCharge}>
                            {charging ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Charging…
                                </>
                            ) : (
                                `Charge ${money(preview?.totalCents ?? 0)} once`
                            )}
                        </Button>
                    </>
                )}
                {step === "result" && (
                    <>
                        <div className="flex-1" />
                        {!result?.charged && (
                            <Button variant="ghost" onClick={() => setStep("card")}>
                                Pick a different card
                            </Button>
                        )}
                        <Button onClick={result?.charged ? onCharged : onClose}>Done</Button>
                    </>
                )}
            </div>
        </Dialog>
    );
}

function CardStep({
    customerId,
    cards,
    chosenCardId,
    onChoose,
    useNewCard,
    onUseNewCard,
    newCardPmId,
    onNewCard,
    keepNewCard,
    setKeepNewCard,
    newCardLabel,
    setNewCardLabel,
}: {
    customerId: string;
    cards: SavedCard[] | null;
    chosenCardId: string | null;
    onChoose: (id: string) => void;
    useNewCard: boolean;
    onUseNewCard: () => void;
    newCardPmId: string | null;
    onNewCard: (id: string) => void;
    keepNewCard: boolean;
    setKeepNewCard: (v: boolean) => void;
    newCardLabel: string;
    setNewCardLabel: (v: string) => void;
}) {
    if (cards === null) {
        return (
            <p className="flex items-center gap-2 text-sm text-foreground/70">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading cards…
            </p>
        );
    }

    return (
        <div className="space-y-3">
            {cards.length > 0 && (
                <>
                    <p className="text-xs font-bold uppercase tracking-wide text-foreground/60">Cards on file</p>
                    {cards.map((c) => (
                        <label
                            key={c.id}
                            className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 ${
                                !useNewCard && chosenCardId === c.id
                                    ? "border-primary bg-primary/5"
                                    : "border-border/70"
                            }`}
                        >
                            <input
                                type="radio"
                                name="batch-card"
                                className="mt-1"
                                checked={!useNewCard && chosenCardId === c.id}
                                onChange={() => onChoose(c.id)}
                            />
                            <span>
                                <span className="block text-sm font-semibold">{cardLabel(c)}</span>
                                <span className="block text-xs text-foreground/60">
                                    {cardExpiry(c) ? `Expires ${cardExpiry(c)}` : "No expiry on file"}
                                    {c.is_default ? " · account default" : ""}
                                </span>
                            </span>
                        </label>
                    ))}
                </>
            )}

            <label
                className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 ${
                    useNewCard ? "border-primary bg-primary/5" : "border-border/70"
                }`}
            >
                <input type="radio" name="batch-card" className="mt-1" checked={useNewCard} onChange={onUseNewCard} />
                <span>
                    <span className="block text-sm font-semibold">
                        {cards.length > 0 ? "Use a different card…" : "Enter a card"}
                    </span>
                    <span className="block text-xs text-foreground/60">
                        They've handed over a purchasing card for this batch.
                    </span>
                </span>
            </label>

            {useNewCard && (
                <div className="ml-6 border-l-2 border-primary/30 pl-4">
                    {newCardPmId ? (
                        <p className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-400">
                            <CheckCircle2 className="h-4 w-4" /> Card ready. It hasn't been charged yet.
                        </p>
                    ) : (
                        <NewCardEntry customerId={customerId} onReady={onNewCard} />
                    )}

                    <p className="mt-4 text-xs font-bold uppercase tracking-wide text-foreground/60">
                        After this charge, what happens to it?
                    </p>
                    <label className="mt-2 flex cursor-pointer items-start gap-3 rounded-lg border border-border/70 p-3">
                        <input
                            type="radio"
                            name="keep-card"
                            className="mt-1"
                            checked={keepNewCard}
                            onChange={() => setKeepNewCard(true)}
                        />
                        <span className="flex-1">
                            <span className="block text-sm font-semibold">Keep it on file for this customer</span>
                            <span className="block text-xs text-foreground/60">
                                Give it a name so it's recognisable next time.
                            </span>
                            {keepNewCard && (
                                <input
                                    className="mt-2 w-full max-w-xs rounded-md border border-input bg-background px-2 py-1 text-sm"
                                    placeholder="e.g. AP purchasing card"
                                    value={newCardLabel}
                                    onChange={(e) => setNewCardLabel(e.target.value)}
                                />
                            )}
                        </span>
                    </label>
                    <label className="mt-2 flex cursor-pointer items-start gap-3 rounded-lg border border-border/70 p-3">
                        <input
                            type="radio"
                            name="keep-card"
                            className="mt-1"
                            checked={!keepNewCard}
                            onChange={() => setKeepNewCard(false)}
                        />
                        <span>
                            <span className="block text-sm font-semibold">Use it once and don't keep it</span>
                            <span className="block text-xs text-foreground/60">
                                Charged for this batch, then dropped. Nothing stored against the account.
                            </span>
                        </span>
                    </label>
                </div>
            )}

            <p className="flex items-start gap-2 rounded-md bg-muted/60 p-3 text-xs text-foreground/70">
                <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                The card number never reaches Groundwork — it goes straight to Stripe from this browser. We only ever
                hold the token, the brand and the last four.
            </p>
        </div>
    );
}

/** Stripe Elements entry, same pattern as the card-on-file panel. */
function NewCardEntry({ customerId, onReady }: { customerId: string; onReady: (pmId: string) => void }) {
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let live = true;
        (async () => {
            try {
                // #1556: the batch screen has its OWN setup-intent route. The
                // shared one is for "save this as the card on file", and the
                // Stripe webhook acts on that — which silently overwrote the
                // account's card with a one-time purchasing card we then
                // detached. This one is stamped for a batch and left alone.
                const r = await api.post<{ clientSecret: string }>(
                    `/api/customers/${customerId}/invoice-batch/setup-intent`,
                    {},
                );
                if (live) setClientSecret(r.clientSecret);
            } catch {
                if (live) setError("Could not start card entry.");
            } finally {
                if (live) setLoading(false);
            }
        })();
        return () => {
            live = false;
        };
    }, [customerId]);

    if (!isStripeConfigured()) {
        return <p className="text-sm text-destructive">Card payments are not configured.</p>;
    }
    if (loading) {
        return (
            <p className="flex items-center gap-2 text-sm text-foreground/70">
                <Loader2 className="h-4 w-4 animate-spin" /> Preparing secure card entry…
            </p>
        );
    }
    if (error || !clientSecret) {
        return <p className="text-sm text-destructive">{error ?? "Could not start card entry."}</p>;
    }
    return (
        <Elements stripe={getStripe()}>
            <CardEntryForm clientSecret={clientSecret} onReady={onReady} />
        </Elements>
    );
}

function CardEntryForm({ clientSecret, onReady }: { clientSecret: string; onReady: (pmId: string) => void }) {
    const stripe = useStripe();
    const elements = useElements();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    async function submit() {
        if (!stripe || !elements) return;
        const el = elements.getElement(CardElement);
        if (!el) return;
        setBusy(true);
        setError(null);
        try {
            const res = await stripe.confirmCardSetup(clientSecret, { payment_method: { card: el } });
            if (res.error) {
                setError(res.error.message ?? "Could not read that card.");
                return;
            }
            const pm = res.setupIntent?.payment_method;
            const id = typeof pm === "string" ? pm : pm?.id;
            if (!id) {
                setError("Could not read that card.");
                return;
            }
            onReady(id);
        } catch {
            setError("Could not read that card.");
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="space-y-3">
            <div className="rounded-md border border-input bg-background px-3 py-3">
                <CardElement options={{ style: { base: { fontSize: "16px" } } }} />
            </div>
            {error && <p className="text-xs text-destructive">{error}</p>}
            <Button type="button" size="sm" variant="outline" disabled={!stripe || busy} onClick={submit}>
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Lock className="mr-2 h-4 w-4" />}
                Use this card
            </Button>
        </div>
    );
}

function ReviewStep({
    preview,
    cardText,
    typed,
    setTyped,
}: {
    preview: PreviewResponse;
    cardText: string;
    typed: string;
    setTyped: (v: string) => void;
}) {
    return (
        <div className="space-y-4">
            <div>
                <p className="mb-2 text-xs font-bold uppercase tracking-wide text-foreground/60">
                    The charge is applied across these {preview.chargeable.length} invoice
                    {preview.chargeable.length === 1 ? "" : "s"}
                </p>
                <div className="max-h-60 overflow-y-auto rounded-lg border border-border/70">
                    <table className="w-full text-sm">
                        <tbody className="divide-y divide-border">
                            {preview.chargeable.map((l) => (
                                <tr key={l.id}>
                                    <td className="px-3 py-2 font-mono text-xs">
                                        {l.invoice_number ?? l.id.slice(0, 8)}
                                    </td>
                                    <td className="px-3 py-2 text-xs text-foreground/70">{l.site_name ?? "—"}</td>
                                    <td className="px-3 py-2 text-right tabular-nums">{money(l.amountCents ?? 0)}</td>
                                </tr>
                            ))}
                            {preview.skipped.map((l) => (
                                <tr key={l.id} className="bg-muted/40 text-foreground/60">
                                    <td className="px-3 py-2 font-mono text-xs">
                                        {l.invoice_number ?? l.id.slice(0, 8)}
                                    </td>
                                    <td className="px-3 py-2 text-xs">{l.site_name ?? "—"}</td>
                                    <td className="px-3 py-2 text-right text-xs">
                                        Skipped — {l.reasonLabel}
                                        {l.reasonDetail && (
                                            <span
                                                className="block text-destructive"
                                                data-testid="batch-skip-card-detail"
                                            >
                                                {l.reasonDetail}
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {preview.skipped.some((l) => l.reasonDetail) && (
                // One try per card (#1984): say why, and what to do instead.
                <p
                    className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm"
                    data-testid="batch-card-skip-note"
                >
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                    <span>
                        <strong>This card already declined on some of these.</strong> We don't try a card twice on the
                        same invoice, so those are skipped. For them: pick a different card, send a new card link, or
                        collect on site.
                    </span>
                </p>
            )}

            {/* States what WILL happen and what WILL NOT, in that order. */}
            <p className="rounded-md border border-emerald-600/30 bg-emerald-600/10 p-3 text-sm">
                <strong>
                    One charge of {money(preview.totalCents)} to {cardText}, applied across {preview.chargeable.length}{" "}
                    invoice{preview.chargeable.length === 1 ? "" : "s"}.
                </strong>
                {preview.skipped.length > 0 && (
                    <>
                        {" "}
                        {preview.skipped.length} of the {preview.selectedCount} you selected{" "}
                        {preview.skipped.length === 1 ? "is" : "are"} skipped and listed above — not charged, not marked
                        paid.
                    </>
                )}
            </p>

            <p className="flex items-start gap-2 rounded-md border border-amber-600/30 bg-amber-600/10 p-3 text-sm">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                    <strong>It's one transaction, so it's all-or-nothing.</strong> If the card declines — a purchasing
                    card with a per-transaction limit is the usual reason at this size — nothing is charged and no
                    invoice is marked paid.
                </span>
            </p>

            {preview.requiresTypedConfirm && (
                <div className="rounded-md border border-amber-600/30 bg-amber-600/10 p-3">
                    <label className="block text-sm">
                        Over $10,000 in one batch — type <strong>{money(preview.totalCents)}</strong> to confirm:
                        <input
                            className="mt-2 w-48 rounded-md border border-input bg-background px-2 py-1 text-sm"
                            value={typed}
                            onChange={(e) => setTyped(e.target.value)}
                            placeholder={(preview.totalCents / 100).toFixed(2)}
                        />
                    </label>
                </div>
            )}
        </div>
    );
}

function ResultStep({ result }: { result: { charged: boolean; totalCents: number; count: number; reason?: string } }) {
    const qc = useQueryClient();
    useEffect(() => {
        if (result.charged) qc.invalidateQueries({ queryKey: ["customer"] });
    }, [result.charged, qc]);

    return result.charged ? (
        <div className="space-y-3">
            <p className="flex items-start gap-2 rounded-md border border-emerald-600/30 bg-emerald-600/10 p-3 text-sm font-semibold">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                {batchResultLine({ charged: true, totalCents: result.totalCents, invoiceCount: result.count })}
            </p>
            <ul className="list-disc space-y-1 pl-5 text-sm text-foreground/75">
                <li>Each invoice carries its share of the one payment, so each still reconciles on its own.</li>
                <li>QuickBooks gets it the way it models it — one payment applied to many invoices.</li>
                <li>
                    Money out of a batch goes back <strong>by check</strong>, not to the card — the card refund is
                    blocked on these invoices on purpose.
                </li>
            </ul>
        </div>
    ) : (
        <div className="space-y-3">
            <p className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm font-semibold text-destructive">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                {batchResultLine({
                    charged: false,
                    totalCents: result.totalCents,
                    invoiceCount: result.count,
                    reason: result.reason,
                })}
            </p>
            <p className="text-sm text-foreground/75">
                We won&apos;t try this card again on these invoices. Pick a different card, send a new card link, or
                collect on site. Purchasing cards often cap a single transaction well below a batch this size.
            </p>
        </div>
    );
}

/** Re-exported so the page can toast a consistent line. */
export { batchResultLine };

/** Kept for callers that want the toast without the dialog. */
export function batchChargedToast(totalCents: number, count: number) {
    toast.success(batchResultLine({ charged: true, totalCents, invoiceCount: count }));
}
